import { DialogComponent } from '@theme/dialog';

/**
 * Promo popup that opens itself (after a delay, on exit intent or on scroll)
 * and remembers when the visitor dismissed it or signed up.
 *
 * Attributes:
 * - trigger: 'delay' | 'exit' | 'scroll'
 * - delay: seconds before opening (also the fallback for exit intent on touch devices)
 * - scroll-percent: how far down the page before opening
 * - frequency-days: days before showing it again once seen, 0 = once per browser session
 * - storage-key: localStorage key, changes when the merchant changes the campaign ID
 * - analytics-name: prefix for Microsoft Clarity events and tags, e.g. promo_popup
 *
 * An optional `.promo-popup__countdown` element counts down to a fixed date (data-end, ISO 8601)
 * or for data-minutes from the visitor's first view (evergreen). data-expired="hide_popup"
 * stops the popup opening once the countdown has ended. An optional `.promo-popup__countdown-progress`
 * bar shrinks from the start (data-start, or the visitor's first view) to the end.
 *
 * @extends DialogComponent
 */
class PromoPopup extends DialogComponent {
  /** @type {number | undefined} */
  #timer;

  /** @type {number | undefined} */
  #countdownInterval;

  /** What the visitor did with the popup this time it was open, for analytics. */
  #outcome = '';

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('dialog:open', this.#startCountdown);
    this.addEventListener('dialog:close', this.#stopCountdown);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleEditorSelect);
      document.addEventListener('shopify:block:select', this.#handleEditorSelect);
      document.addEventListener('shopify:section:deselect', this.#handleEditorDeselect);
      return;
    }

    // Remember the popup as soon as it opens, not only on close: following its link, reloading or
    // going back leaves the page with the popup still open, and it would reopen on the next page.
    this.addEventListener('dialog:open', this.#rememberSeen);
    this.addEventListener('dialog:close', this.#rememberSeen);
    this.refs.dialog.addEventListener('submit', this.#handleSubmit);

    this.addEventListener('dialog:open', this.#trackOpen);
    this.addEventListener('dialog:close', this.#trackClose);
    this.refs.dialog.addEventListener('click', this.#trackClick);

    // The signup form reloads the page; reopen so the visitor sees the success or error message.
    if (this.#consumePendingSubmit()) {
      if (new URLSearchParams(location.search).get('customer_posted') === 'true') {
        this.#write(this.#subscribedKey, '1');
      }
      this.#open();
      return;
    }

    if (this.#isSuppressed()) return;

    if (this.#countdown?.dataset.expired === 'hide_popup' && this.#remainingMs() === 0) return;

    this.#armTrigger();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disarmTrigger();
    this.#stopCountdown();
    this.removeEventListener('dialog:open', this.#startCountdown);
    this.removeEventListener('dialog:close', this.#stopCountdown);
    this.removeEventListener('dialog:open', this.#rememberSeen);
    this.removeEventListener('dialog:close', this.#rememberSeen);
    document.removeEventListener('shopify:section:select', this.#handleEditorSelect);
    document.removeEventListener('shopify:block:select', this.#handleEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleEditorDeselect);
  }

  get #storageKey() {
    return this.getAttribute('storage-key') || 'promo-popup';
  }

  get #subscribedKey() {
    return `${this.#storageKey}:subscribed`;
  }

  get #frequencyDays() {
    return Number(this.getAttribute('frequency-days')) || 0;
  }

  #isSuppressed() {
    if (this.hasAttribute('hide-after-signup') && this.#read(this.#subscribedKey)) return true;

    if (this.#frequencyDays === 0) {
      return Boolean(this.#read(this.#storageKey, sessionStorage));
    }

    const dismissedAt = Number(this.#read(this.#storageKey));
    if (!dismissedAt) return false;

    return Date.now() - dismissedAt < this.#frequencyDays * 24 * 60 * 60 * 1000;
  }

  #armTrigger() {
    const trigger = this.getAttribute('trigger');
    const delayMs = (Number(this.getAttribute('delay')) || 0) * 1000;
    const isTouch = window.matchMedia('(hover: none)').matches;

    if (trigger === 'exit' && !isTouch) {
      document.addEventListener('mouseout', this.#handleMouseOut);
    } else if (trigger === 'scroll') {
      window.addEventListener('scroll', this.#handleScroll, { passive: true });
      this.#handleScroll();
    } else {
      this.#timer = window.setTimeout(() => this.#open(), delayMs);
    }
  }

  #disarmTrigger() {
    window.clearTimeout(this.#timer);
    document.removeEventListener('mouseout', this.#handleMouseOut);
    window.removeEventListener('scroll', this.#handleScroll);
  }

  /** @param {MouseEvent} event */
  #handleMouseOut = (event) => {
    if (event.relatedTarget || event.clientY > 0) return;
    this.#open();
  };

  #handleScroll = () => {
    const percent = Number(this.getAttribute('scroll-percent')) || 50;
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;

    if ((window.scrollY / scrollable) * 100 >= percent) this.#open();
  };

  /**
   * Opens the popup, waiting for any other open dialog (cart drawer, search, quick add) to close first.
   */
  #open() {
    this.#disarmTrigger();

    if (this.minWidth && window.innerWidth < this.minWidth) return;

    if (document.querySelector('dialog[open]')) {
      this.#timer = window.setTimeout(() => this.#open(), 2000);
      return;
    }

    this.showDialog();
  }

  #rememberSeen = () => {
    if (this.#frequencyDays === 0) {
      this.#write(this.#storageKey, '1', sessionStorage);
    } else {
      this.#write(this.#storageKey, String(Date.now()));
    }
  };

  #handleSubmit = () => {
    this.#write(`${this.#storageKey}:pending`, '1', sessionStorage);
    this.#outcome = 'signed_up';
    this.#track('signup', 'signed_up');
  };

  /*
   * Microsoft Clarity: an event and a session tag for each step, so heatmaps and recordings can be
   * filtered to visitors who saw the popup. Sessions where it opens are upgraded so Clarity keeps
   * their recordings. Does nothing when Clarity is not installed or in the theme editor.
   */

  get #analyticsName() {
    return this.getAttribute('analytics-name') || 'promo_popup';
  }

  /**
   * @param {string} event
   * @param {string} [tag]
   */
  #track(event, tag) {
    const clarity = /** @type {any} */ (window).clarity;
    if (window.Shopify?.designMode || typeof clarity !== 'function') return;

    const name = this.#analyticsName;
    try {
      clarity('event', `${name}_${event}`);
      if (tag) clarity('set', name, tag);
    } catch {}
  }

  #trackOpen = () => {
    this.#outcome = '';
    this.#track('open', 'opened');

    const clarity = /** @type {any} */ (window).clarity;
    if (typeof clarity === 'function' && !window.Shopify?.designMode) {
      try {
        clarity('upgrade', `${this.#analyticsName} opened`);
      } catch {}
    }
  };

  #trackClose = () => {
    if (!this.#outcome) this.#track('dismiss', 'dismissed');
  };

  /** @param {MouseEvent} event */
  #trackClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || !this.refs.dialog.contains(link)) return;

    this.#outcome = 'clicked';
    this.#track('click', 'clicked');
  };

  #consumePendingSubmit() {
    const key = `${this.#storageKey}:pending`;
    const pending = this.#read(key, sessionStorage);
    if (pending) {
      try {
        sessionStorage.removeItem(key);
      } catch {}
    }
    return Boolean(pending);
  }

  /** @param {Event & { detail?: { sectionId?: string } }} event */
  #handleEditorSelect = (event) => {
    if (event.detail?.sectionId === this.dataset.sectionId) this.showDialog();
  };

  /** @param {Event & { detail?: { sectionId?: string } }} event */
  #handleEditorDeselect = (event) => {
    if (event.detail?.sectionId === this.dataset.sectionId) this.closeDialog();
  };

  get #countdown() {
    const element = this.querySelector('.promo-popup__countdown');
    return element instanceof HTMLElement ? element : null;
  }

  get #countdownStartKey() {
    return `${this.#storageKey}:countdown-start`;
  }

  /**
   * Milliseconds left on the countdown, 0 when it has ended, or null when there is no valid countdown.
   * An evergreen countdown that has not started yet reports its full duration.
   */
  #remainingMs() {
    const countdown = this.#countdown;
    if (!countdown) return null;

    let end;
    if (countdown.dataset.mode === 'evergreen') {
      const duration = (Number(countdown.dataset.minutes) || 0) * 60 * 1000;
      const start = Number(this.#read(this.#countdownStartKey)) || Date.now();
      end = start + duration;
    } else {
      end = Date.parse(countdown.dataset.end || '');
    }

    if (Number.isNaN(end)) return null;
    return Math.max(0, end - Date.now());
  }

  #startCountdown = () => {
    const countdown = this.#countdown;
    if (!countdown) return;

    // First view: the start of an evergreen countdown, and of the progress bar when no start date is set.
    if (!this.#read(this.#countdownStartKey)) {
      this.#write(this.#countdownStartKey, String(Date.now()));
    }

    this.#stopCountdown();
    this.#renderCountdown();
    this.#countdownInterval = window.setInterval(() => this.#renderCountdown(), 1000);
  };

  #stopCountdown = () => {
    window.clearInterval(this.#countdownInterval);
  };

  #renderCountdown() {
    const countdown = this.#countdown;
    if (!countdown) return;

    const remaining = this.#remainingMs();
    if (remaining === null || remaining === 0) {
      countdown.hidden = true;
      this.#stopCountdown();
      return;
    }

    countdown.hidden = false;
    this.#renderProgress(countdown, remaining);

    const totalSeconds = Math.floor(remaining / 1000);
    const values = {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };

    for (const [unit, value] of Object.entries(values)) {
      const segment = countdown.querySelector(`[data-unit="${unit}"]`);
      if (!(segment instanceof HTMLElement)) continue;

      // Drop the days segment once there is less than a day left.
      if (unit === 'days') segment.hidden = value === 0;

      const number = segment.querySelector('.promo-popup__countdown-value');
      if (number) number.textContent = String(value).padStart(2, '0');
    }
  }

  /**
   * @param {HTMLElement} countdown
   * @param {number} remaining
   */
  #renderProgress(countdown, remaining) {
    const progress = countdown.querySelector('.promo-popup__countdown-progress');
    if (!(progress instanceof HTMLElement)) return;

    let total;
    if (countdown.dataset.mode === 'evergreen') {
      total = (Number(countdown.dataset.minutes) || 0) * 60 * 1000;
    } else {
      const end = Date.parse(countdown.dataset.end || '');
      const startDate = Date.parse(countdown.dataset.start || '');
      const start = Number.isNaN(startDate) ? Number(this.#read(this.#countdownStartKey)) : startDate;
      total = end - start;
    }

    if (!(total > 0)) {
      progress.hidden = true;
      return;
    }

    progress.hidden = false;
    const ratio = Math.min(1, Math.max(0, remaining / total));
    progress.style.setProperty('--promo-popup-progress', ratio.toFixed(4));
  }

  /**
   * Copies the discount code to the clipboard.
   * @param {Event} event
   */
  copyCode = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-code]') : null;
    if (!(button instanceof HTMLElement) || !button.dataset.code) return;
    const code = button.dataset.code;

    if (!(await this.#writeClipboard(code))) return;

    if (!this.#outcome) this.#outcome = 'copied_code';
    this.#track('copy_code', 'copied_code');

    const label = button.querySelector('.promo-popup__copy-label, [data-copy-label]');
    if (!label) return;

    const original = label.textContent;
    label.textContent = button.dataset.copiedLabel || original;
    button.toggleAttribute('data-copied', true);
    window.setTimeout(() => {
      label.textContent = original;
      button.removeAttribute('data-copied');
    }, 2000);
  };

  /**
   * Copies text, falling back to execCommand where the Clipboard API is blocked
   * (in-app browsers, embedded webviews, older Safari).
   * @param {string} text
   */
  async #writeClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {}

    // Inside the open modal dialog, so the textarea can take focus.
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.cssText = 'position:fixed;opacity:0;pointer-events:none;inset:0 auto auto 0;';
    this.refs.dialog.append(textarea);
    textarea.select();

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch {}

    textarea.remove();
    return copied;
  }

  /**
   * @param {string} key
   * @param {Storage} [storage]
   */
  #read(key, storage = localStorage) {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * @param {string} key
   * @param {string} value
   * @param {Storage} [storage]
   */
  #write(key, value, storage = localStorage) {
    try {
      storage.setItem(key, value);
    } catch {}
  }
}

if (!customElements.get('promo-popup-component')) customElements.define('promo-popup-component', PromoPopup);
